import React, { createContext, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';

// Create a context for the toast
export const ToastContext = createContext(null);

// Create a provider component
export const ToastProvider = ({ children }) => {
    const [toast, setToast] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // Create a stable reference to the showToast function
    const showToast = useCallback((message, severity = 'success') => {

        if (!message) {
            message = 'An unknown error occurred';
        }
        const newToast = {
            open: true,
            message,
            severity,
        };
        setToast(newToast);
    }, [toast]);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setToast(prev => ({ ...prev, open: false }));
    };

    const contextValue = React.useMemo(() => {
        return {
            showToast: (message, severity) => {
                return showToast(message, severity);
            },
            showSuccess: (message) => {
                return showToast(message, 'success');
            },
            showError: (message) => {
                return showToast(message, 'error');
            },
            showInfo: (message) => {
                return showToast(message, 'info');
            },
            showWarning: (message) => {
                return showToast(message, 'warning');
            },
        };
    }, [showToast]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '300px',
                        '&.MuiAlert-filledSuccess': {
                            backgroundColor: '#4BCE97',
                            color: '#fff',
                        },
                        '&.MuiAlert-filledError': {
                            backgroundColor: '#FF5252',
                            color: '#fff',
                        },
                        '&.MuiAlert-filledInfo': {
                            backgroundColor: '#2196F3',
                            color: '#fff',
                        },
                        '&.MuiAlert-filledWarning': {
                            backgroundColor: '#FFC107',
                            color: '#000',
                        },
                    },
                    zIndex: 1400, // Ensure it's above other content
                    marginTop: '20px',
                    marginRight: '20px'
                }}
            >
                <Alert
                    onClose={handleClose}
                    severity={toast.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        '& .MuiAlert-message': {
                            padding: '8px 0',
                        }
                    }}
                    elevation={6}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
};


export const ToastContainer = ({ children }) => (
    <ToastProvider>{children}</ToastProvider>
);

// Default export with all toast methods
export default {
    Provider: ToastProvider,
    ToastContainer,
};
